import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, CheckCircle, Envelope } from "@phosphor-icons/react";

export default function NewsletterForm({ variant = "stripe" }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // "ok" | "already" | null
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/newsletter/subscribe", { email, source: variant });
      setStatus(res.data.status === "already_subscribed" ? "already" : "ok");
      setEmail("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't subscribe — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Compact variant — used in footer
  if (variant === "footer") {
    return (
      <form onSubmit={submit} data-testid="newsletter-form-footer" className="w-full">
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4FF00]">Friday Roundup</label>
        <p className="mt-2 text-sm text-white/60">The smartest tennis newsletter. Free, weekly.</p>
        <div className="mt-4 flex border border-white/15 focus-within:border-[#D4FF00]">
          <input
            data-testid="newsletter-email-footer"
            type="email"
            required
            placeholder="you@inbox.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-transparent px-3 py-3 text-sm text-white placeholder-white/40 outline-none"
          />
          <button
            data-testid="newsletter-submit-footer"
            type="submit"
            disabled={submitting}
            className="flex items-center gap-1 bg-[#D4FF00] px-4 text-[10px] font-bold uppercase tracking-[0.2em] text-black hover:-translate-y-0.5 disabled:opacity-50"
          >
            {submitting ? "…" : <>Join <ArrowRight size={12} weight="bold" /></>}
          </button>
        </div>
        {status === "ok" && <p className="mt-2 flex items-center gap-1 text-xs text-[#D4FF00]"><CheckCircle size={14} weight="bold" /> You're on the list.</p>}
        {status === "already" && <p className="mt-2 text-xs text-white/60">You're already subscribed — thanks!</p>}
        {error && <p data-testid="newsletter-error-footer" className="mt-2 text-xs text-red-400">{error}</p>}
      </form>
    );
  }

  // Default — bold yellow stripe (homepage)
  return (
    <section data-testid="newsletter-form-stripe" className="bg-[#D4FF00] py-12 sm:py-16">
      <div className="mx-auto max-w-[1400px] px-4 text-black sm:px-6 lg:px-12">
        <div className="grid gap-6 md:grid-cols-2 md:items-center md:gap-8">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] sm:text-xs">
              <Envelope size={14} weight="bold" /> Friday Roundup
            </div>
            <h3 className="mt-3 max-w-2xl font-display text-2xl font-bold leading-[0.95] tracking-tighter sm:text-3xl md:text-4xl lg:text-5xl">
              The smartest tennis newsletter. Fridays at 6pm.
            </h3>
            <p className="mt-3 max-w-xl text-sm">A weekly drop of the best Baseline Breakdown coverage. No spam. Unsubscribe in one click.</p>
          </div>
          <form onSubmit={submit} className="flex flex-col gap-3">
            <div className="flex flex-col border-2 border-black bg-black sm:flex-row">
              <input
                data-testid="newsletter-email-stripe"
                type="email"
                required
                placeholder="you@inbox.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-black px-4 py-3 text-base text-white placeholder-white/40 outline-none sm:py-4"
              />
              <button
                data-testid="newsletter-submit-stripe"
                type="submit"
                disabled={submitting}
                className="flex items-center justify-center gap-2 bg-[#D4FF00] px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-black transition-transform hover:-translate-y-0.5 disabled:opacity-50 sm:py-4"
              >
                {submitting ? "Joining…" : <>Subscribe <ArrowRight size={14} weight="bold" /></>}
              </button>
            </div>
            {status === "ok" && (
              <p className="flex items-center gap-1 text-sm font-semibold"><CheckCircle size={16} weight="bold" /> You're on the list.</p>
            )}
            {status === "already" && <p className="text-sm">You're already subscribed — thanks!</p>}
            {error && <p data-testid="newsletter-error-stripe" className="text-sm text-red-700">{error}</p>}
          </form>
        </div>
      </div>
    </section>
  );
}
